import * as THREE from "three";
import type { DishKind } from "../game/dishes";
import { DRINKS, DRINK_NAMES, TOPPINGS, TOPPING_NAMES } from "../game/food";
import { BURGER_SHACK, COUNTER_TOP, slotCentre, type Box, type Station } from "../game/kitchen";
import { box, paint, span, spanBox, steel, type Room } from "./build";
import { toppingPieces } from "./food";
import type { RoomLook } from "./looks";
import { FRONT } from "../game/customers";
import { BOARD_SPOTS } from "../game/shift";
import { buildBoardwalk } from "./boardwalk";
import { binLabels, checkerTile, kioskScreen, menuBoard, sodaLabels, subwayTile } from "./textures";

// The Burger Shack, built once from `kitchen.ts`'s `BURGER_SHACK` layout: a
// small kitchen with a checkered floor, the cooking line along the back wall
// under a steel hood (fridge, flat-top, fryer, freezer, trash), the basket
// shelf, assembly board and topping rail along the front, the pickup window
// onto the boardwalk (`boardwalk.ts`), and the dish pit up the right wall.

const L = BURGER_SHACK;
const S = L.stations as Record<Station, Box>;
const { width: W, depth: D, height: H } = L;

export function buildShack(restaurantName: string, look: RoomLook): Room {
  const g = new THREE.Group();
  const highlight: Partial<Record<Station, THREE.MeshStandardMaterial[]>> = {};
  const lit = (station: Station, m: THREE.MeshStandardMaterial) => {
    (highlight[station] ??= []).push(m);
    return m;
  };

  // --- floor, walls, ceiling --------------------------------------------------
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), new THREE.MeshStandardMaterial({ map: checkerTile(W, D), roughness: 0.55 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(W / 2, 0, D / 2);
  floor.receiveShadow = true;
  g.add(floor);

  const wall = (w: number) => new THREE.MeshStandardMaterial({ map: subwayTile(w, H), roughness: 0.35 });
  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wall(W));
  back.position.set(W / 2, H / 2, 0);
  g.add(back);
  const left = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wall(D));
  left.rotation.y = Math.PI / 2;
  left.position.set(0, H / 2, D / 2);
  g.add(left);
  const right = left.clone();
  right.rotation.y = -Math.PI / 2;
  right.position.x = W;
  g.add(right);
  // The front wall, around the pickup window.
  const { x0, x1, y0, y1 } = L.opening;
  const frontMat = paint(0xf1e6cf, 0.8);
  for (const [a, b, c, d] of [
    [0, x0, 0, H],
    [x1, W, 0, H],
    [x0, x1, 0, y0],
    [x0, x1, y1, H],
  ] as const) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(b - a, d - c), frontMat);
    m.rotation.y = Math.PI;
    m.position.set((a + b) / 2, (c + d) / 2, D);
    g.add(m);
  }
  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(W, D), paint(0xf4f1ea));
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(W / 2, H, D / 2);
  g.add(ceiling);
  const panel = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xfff6e0, emissiveIntensity: 1.2 });
  span(2.2, 3.6, H - 0.05, H, 1.9, 2.15, panel, g);
  span(0, W, 0, 0.1, 0, 0.02, paint(0x2b2d42), g); // baseboard

  // --- outside the window: the boardwalk, the beach and the rides ------------------
  const outside = buildBoardwalk(D);
  g.add(outside.group);
  // The tablet ordering kiosk out front, beside the window.
  const kiosk = new THREE.Group();
  const kioskMat = new THREE.MeshLambertMaterial({ color: 0x2b2d42, emissive: 0x2b2d42, emissiveIntensity: 0.4 });
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.1, 0.12), kioskMat);
  post.position.y = 0.55;
  const foot = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.04, 0.32), kioskMat);
  foot.position.y = 0.02;
  const screen = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.36, 0.04), [kioskMat, kioskMat, kioskMat, kioskMat, new THREE.MeshBasicMaterial({ map: kioskScreen() }), kioskMat]);
  screen.position.set(0, 1.22, 0.03);
  screen.rotation.x = -0.35;
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.12), new THREE.MeshBasicMaterial({ color: 0xd8392b }));
  sign.position.set(0, 1.5, 0.02);
  kiosk.add(post, foot, screen, sign);
  kiosk.position.set(FRONT.kioskStand.x, 0, FRONT.kioskStand.z);
  g.add(kiosk);

  // --- the cooking line ------------------------------------------------------------
  // A steel hood over the flat-top and fryer.
  span(0.9, 3.0, 2.15, 2.5, 0, 0.9, steel(), g);
  // Fridge: a reach-in, door facing the room.
  const fridge = lit("fridge", steel());
  spanBox(S.fridge, fridge, g);
  span(S.fridge.min[0] + 0.05, S.fridge.max[0] - 0.05, 0.1, 1.8, S.fridge.max[2], S.fridge.max[2] + 0.01, paint(0xb7c0c7, 0.3), g);
  span(S.fridge.max[0] - 0.12, S.fridge.max[0] - 0.09, 0.9, 1.4, S.fridge.max[2] + 0.01, S.fridge.max[2] + 0.04, paint(0x2b2d42, 0.4), g);
  label(g, "PATTIES", (S.fridge.min[0] + S.fridge.max[0]) / 2, 1.55, S.fridge.max[2] + 0.012);

  // Flat-top: a cabinet, the dark steel plate, a splash guard and knobs.
  const top = S.griddle;
  const plate = lit("griddle", new THREE.MeshStandardMaterial({ color: 0x3a3b3d, metalness: 0.65, roughness: 0.45 }));
  span(top.min[0], top.max[0], 0, top.min[1], top.min[2], top.max[2], steel(), g);
  span(top.min[0], top.max[0], top.min[1], 0.9, top.min[2], top.max[2], plate, g);
  span(top.min[0], top.max[0], 0.9, 1.12, top.min[2], top.min[2] + 0.03, steel(), g);
  for (let i = 0; i < 4; i++) {
    const knob = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.03, 12), paint(0x111111, 0.4));
    knob.rotation.x = Math.PI / 2;
    knob.position.set(top.min[0] + 0.15 + i * 0.3, 0.8, top.max[2] + 0.015);
    g.add(knob);
  }
  // Fryer: two wells of hot oil.
  const fr = S.fryer;
  const fryerMat = lit("fryer", steel());
  span(fr.min[0], fr.max[0], 0, 0.84, fr.min[2], fr.max[2], fryerMat, g);
  const oil = new THREE.MeshStandardMaterial({ color: 0xb07a1c, roughness: 0.15, metalness: 0.3 });
  const wellW = (fr.max[0] - fr.min[0]) / 2;
  for (let i = 0; i < 2; i++) {
    const wx0 = fr.min[0] + i * wellW;
    span(wx0, wx0 + 0.02, 0.84, 0.94, fr.min[2], fr.max[2], fryerMat, g);
    span(wx0 + wellW - 0.02, wx0 + wellW, 0.84, 0.94, fr.min[2], fr.max[2], fryerMat, g);
    span(wx0, wx0 + wellW, 0.84, 0.94, fr.min[2], fr.min[2] + 0.02, fryerMat, g);
    span(wx0, wx0 + wellW, 0.84, 0.94, fr.max[2] - 0.02, fr.max[2], fryerMat, g);
    span(wx0 + 0.02, wx0 + wellW - 0.02, 0.84, 0.865, fr.min[2] + 0.02, fr.max[2] - 0.02, oil, g);
  }
  // Freezer: a white chest with a lid.
  const fz = S.freezer;
  const freezer = lit("freezer", paint(0xf3f4f2, 0.4));
  spanBox(fz, freezer, g);
  span(fz.min[0] + 0.02, fz.max[0] - 0.02, fz.max[1] - 0.06, fz.max[1] - 0.05, fz.max[2], fz.max[2] + 0.005, paint(0x9aa3ab), g);
  span(fz.min[0] + 0.2, fz.max[0] - 0.2, fz.max[1] - 0.1, fz.max[1] - 0.07, fz.max[2], fz.max[2] + 0.03, paint(0x2b2d42, 0.4), g);
  label(g, "FRIES", (fz.min[0] + fz.max[0]) / 2, 0.6, fz.max[2] + 0.006);
  // Trash.
  const tr = S.trash;
  const can = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.19, tr.max[1], 20), lit("trash", paint(0x4a5560, 0.6)));
  can.position.set((tr.min[0] + tr.max[0]) / 2, tr.max[1] / 2, (tr.min[2] + tr.max[2]) / 2);
  can.castShadow = true;
  g.add(can);
  const lid = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.02, 8, 24), paint(0x111111, 0.4));
  lid.rotation.x = Math.PI / 2;
  lid.position.set(can.position.x, tr.max[1], can.position.z);
  g.add(lid);

  // --- along the front: basket shelf, assembly board, toppings ---------------------
  const sh = S.shelf;
  const wire = lit("shelf", new THREE.MeshStandardMaterial({ color: 0xd8dde2, metalness: 0.6, roughness: 0.4 }));
  const shelfLevels = [0.1, 0.55, 1.0, 1.45];
  for (const [x, z] of [
    [sh.min[0], sh.min[2]],
    [sh.min[0], sh.max[2]],
    [sh.max[0], sh.min[2]],
    [sh.max[0], sh.max[2]],
  ] as const) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, sh.max[1], 8), wire);
    post.position.set(x, sh.max[1] / 2, z);
    g.add(post);
  }
  for (const y of shelfLevels) span(sh.min[0], sh.max[0], y - 0.01, y, sh.min[2], sh.max[2], wire, g);

  const bd = S.board;
  span(bd.min[0] - 0.05, 3.05, 0, COUNTER_TOP - 0.02, bd.min[2] - 0.02, D, steel(), g);
  const cutting = lit("board", paint(0xf7f7f2, 0.6));
  span(bd.min[0] + 0.05, bd.max[0] - 0.05, COUNTER_TOP - 0.02, bd.max[1] - 0.075, bd.min[2] + 0.02, bd.max[2], cutting, g);
  // The cold rail: six pans of toppings, with their names along the front.
  const rail = steel();
  span(bd.min[0], bd.max[0], COUNTER_TOP - 0.02, 0.97, 4.06, D, rail, g);
  for (const t of TOPPINGS) {
    const b = S[`bin:${t}`];
    const pan = lit(`bin:${t}`, paint(0xdfe4e8, 0.3));
    span(b.min[0] + 0.01, b.max[0] - 0.01, 0.97, 0.98, b.min[2] + 0.01, b.max[2] - 0.01, pan, g);
    for (let k = 0; k < 4; k++) {
      const piece = toppingPieces(t);
      piece.scale.setScalar(0.65);
      piece.position.set(b.min[0] + 0.06 + (k % 2) * 0.1, 0.982 + Math.floor(k / 2) * 0.006, b.min[2] + 0.07 + Math.floor(k / 2) * 0.11);
      piece.rotation.y = k * 0.7;
      g.add(piece);
    }
  }
  const labels = new THREE.Mesh(new THREE.PlaneGeometry(bd.max[0] - bd.min[0], 0.04), new THREE.MeshBasicMaterial({ map: binLabels(TOPPINGS.map((t) => TOPPING_NAMES[t])) }));
  labels.rotation.y = Math.PI;
  labels.position.set((bd.min[0] + bd.max[0]) / 2, 0.95, 4.058);
  g.add(labels);

  // --- the soda machine, against the left wall ---------------------------------------
  // A counter, the fountain cabinet with a nozzle per drink over a drip tray,
  // a label under each, and the clean cups stacked upside down on top.
  const nozzleBoxes = DRINKS.map((d) => S[`soda:${d}`]);
  const sz0 = Math.min(...nozzleBoxes.map((b) => b.min[2]));
  const sz1 = Math.max(...nozzleBoxes.map((b) => b.max[2]));
  span(0, 0.62, 0, COUNTER_TOP - 0.02, 1.72, 2.88, steel(), g);
  const cabinet = paint(0xc8261f, 0.5);
  span(0.02, 0.4, COUNTER_TOP, 1.75, sz0 - 0.02, sz1 + 0.02, cabinet, g);
  span(0.4, 0.58, COUNTER_TOP, COUNTER_TOP + 0.025, sz0, sz1, paint(0x2b2d42, 0.4), g); // drip tray
  const sodaLabel = new THREE.Mesh(new THREE.PlaneGeometry(sz1 - sz0, 0.07), new THREE.MeshBasicMaterial({ map: sodaLabels(DRINKS.map((d) => DRINK_NAMES[d])) }));
  sodaLabel.rotation.y = Math.PI / 2;
  sodaLabel.position.set(0.402, 1.52, (sz0 + sz1) / 2);
  g.add(sodaLabel);
  for (const d of DRINKS) {
    const b = S[`soda:${d}`];
    const zc = (b.min[2] + b.max[2]) / 2;
    const head = lit(`soda:${d}`, steel());
    span(0.4, 0.5, 1.36, 1.42, zc - 0.05, zc + 0.05, head, g);
    const spout = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.015, 0.05, 10), steel());
    spout.position.set(0.46, 1.33, zc);
    g.add(spout);
    // The lever you'd push a cup against.
    span(0.47, 0.49, 1.18, 1.34, zc - 0.012, zc + 0.012, paint(0x2b2d42, 0.4), g);
  }

  // --- the pickup window -----------------------------------------------------------
  const ledge = lit("window", steel());
  span(x0, x1, y0 - 0.05, y0, 4.1, D + 0.25, ledge, g);
  span(3.05, x0, 0, COUNTER_TOP - 0.02, 4.1, D, steel(), g);
  span(x0, 4.6, 0, y0 - 0.05, 4.1, D, steel(), g);
  for (const x of [x0, x1]) span(x - 0.01, x + 0.01, y0, y1, D - 0.02, D + 0.12, paint(0xe8e6de), g);
  span(x0, x1, y1 - 0.01, y1 + 0.01, D - 0.02, D + 0.12, paint(0xe8e6de), g);
  const ticketRail = new THREE.Vector3((x0 + x1) / 2 - 0.15, y1 - 0.04, D - 0.03);
  span(x0 + 0.05, x1 - 0.05, y1 - 0.03, y1 - 0.01, D - 0.04, D - 0.02, steel(), g);
  // The order-up bell.
  const bell = new THREE.Group();
  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.045, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0xd9b44a, metalness: 0.8, roughness: 0.25 }));
  bell.add(dome);
  const plunger = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.03, 8), paint(0x2b2d42, 0.3));
  plunger.position.y = 0.055;
  bell.add(plunger);
  const bellBase = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.008, 16), paint(0x2b2d42, 0.4));
  bellBase.position.y = -0.002;
  bell.add(bellBase);
  bell.position.set(x1 - 0.12, y0, 4.25);
  g.add(bell);
  // The menu board on the left wall, between the soda machine and the basket
  // shelf and above both, where it isn't hidden behind anything.
  const menu = new THREE.Mesh(new THREE.PlaneGeometry(0.84, 0.64), new THREE.MeshStandardMaterial({ map: menuBoard(restaurantName, look.specials), roughness: 0.8 }));
  menu.rotation.y = Math.PI / 2;
  menu.position.set(0.01, 1.9, 3.31);
  g.add(menu);

  // --- the dish pit, up the right wall ---------------------------------------------
  const tb = S.tub;
  span(4.6, W, 0, COUNTER_TOP, 3.75, D, steel(), g);
  const tubMat = lit("tub", paint(0x6f7a84, 0.55));
  const [tx0, tx1, tz0, tz1, ty0, ty1] = [tb.min[0], tb.max[0], tb.min[2], tb.max[2], COUNTER_TOP, COUNTER_TOP + 0.18];
  span(tx0, tx1, ty0, ty0 + 0.015, tz0, tz1, tubMat, g);
  span(tx0, tx1, ty0, ty1, tz0, tz0 + 0.015, tubMat, g);
  span(tx0, tx1, ty0, ty1, tz1 - 0.015, tz1, tubMat, g);
  span(tx0, tx0 + 0.015, ty0, ty1, tz0, tz1, tubMat, g);
  span(tx1 - 0.015, tx1, ty0, ty1, tz0, tz1, tubMat, g);

  // Sink: a counter with a basin, the faucet against the wall.
  const sk = S.sink;
  const sinkMat = lit("sink", steel());
  const [bx0, bx1, bz0, bz1, bb] = [sk.min[0] + 0.06, sk.max[0] - 0.06, sk.min[2] + 0.06, sk.max[2] - 0.06, 0.62];
  span(5.12, W, 0, COUNTER_TOP - 0.04, 0.55, 3.45, paint(0x8a939b, 0.5), g); // the dish line's cabinets
  span(5.12, W, COUNTER_TOP - 0.04, COUNTER_TOP, sk.min[2], bz0, sinkMat, g);
  span(5.12, W, COUNTER_TOP - 0.04, COUNTER_TOP, bz1, sk.max[2] + 0.05, sinkMat, g);
  span(5.12, bx0, COUNTER_TOP - 0.04, COUNTER_TOP, bz0, bz1, sinkMat, g);
  span(bx1, W, COUNTER_TOP - 0.04, COUNTER_TOP, bz0, bz1, sinkMat, g);
  span(bx0, bx1, bb, bb + 0.01, bz0, bz1, sinkMat, g);
  span(bx0, bx0 + 0.01, bb, COUNTER_TOP, bz0, bz1, sinkMat, g);
  span(bx1 - 0.01, bx1, bb, COUNTER_TOP, bz0, bz1, sinkMat, g);
  span(bx0, bx1, bb, COUNTER_TOP, bz0, bz0 + 0.01, sinkMat, g);
  span(bx0, bx1, bb, COUNTER_TOP, bz1 - 0.01, bz1, sinkMat, g);
  const water = new THREE.Mesh(new THREE.PlaneGeometry(bx1 - bx0 - 0.02, bz1 - bz0 - 0.02), new THREE.MeshStandardMaterial({ color: 0x9fd3e6, transparent: true, opacity: 0.55, roughness: 0.1 }));
  water.rotation.x = -Math.PI / 2;
  water.position.set((bx0 + bx1) / 2, bb + 0.12, (bz0 + bz1) / 2);
  g.add(water);
  const pipe = steel();
  box(0.025, 0.2, 0.025, pipe, W - 0.05, COUNTER_TOP + 0.1, (bz0 + bz1) / 2, g);
  box(0.18, 0.025, 0.025, pipe, W - 0.13, COUNTER_TOP + 0.2, (bz0 + bz1) / 2, g);
  span(W - 0.02, W, COUNTER_TOP, 1.2, 0.55, 3.45, sinkMat, g); // backsplash

  // Drainboard with the rack on it.
  const rk = S.rack;
  span(5.12, W, COUNTER_TOP - 0.04, COUNTER_TOP, rk.min[2] - 0.05, rk.max[2] + 0.05, lit("rack", steel()), g);

  // The dish machine: a cabinet, a hood that lifts, a status lamp facing the room.
  const m = S.machine;
  const machineMat = lit("machine", steel());
  span(m.min[0], m.max[0], 0, 0.85, m.min[2], m.max[2], machineMat, g);
  span(m.min[0], m.max[0], 1.85, 2.0, m.min[2] + 0.1, m.max[2] - 0.1, machineMat, g);
  const hood = new THREE.Group();
  const hoodMat = lit("machine", steel());
  span(m.min[0], m.max[0], 0.86, 1.37, m.min[2], m.min[2] + 0.02, hoodMat, hood);
  span(m.min[0], m.max[0], 0.86, 1.37, m.max[2] - 0.02, m.max[2], hoodMat, hood);
  span(m.min[0], m.min[0] + 0.02, 0.86, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.max[0] - 0.02, m.max[0], 0.86, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.min[0], m.max[0], 1.35, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.min[0] - 0.06, m.min[0], 1.2, 1.23, m.min[2] + 0.2, m.max[2] - 0.2, paint(0x2b2d42, 0.4), hood); // handle
  g.add(hood);
  for (const z of [m.min[2] + 0.02, m.max[2] - 0.04]) span(m.max[0] - 0.06, m.max[0] - 0.02, 0.85, 1.85, z, z + 0.02, machineMat, g);
  const lamp = new THREE.MeshStandardMaterial({ color: 0x333333, emissive: 0x22c55e, emissiveIntensity: 1 });
  box(0.02, 0.05, 0.05, lamp, m.min[0] - 0.01, 0.75, m.max[2] - 0.1, g);
  const chamber = paint(0x59636c, 0.4);
  span(m.min[0] + 0.03, m.max[0] - 0.03, 0.85, 0.86, m.min[2] + 0.03, m.max[2] - 0.03, chamber, g);

  // A wall clock over the window.
  const face = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.03, 32), paint(0xfdfdf8, 0.5));
  face.rotation.x = Math.PI / 2;
  const cx = 1.0;
  const cy = 2.25;
  face.position.set(cx, cy, D - 0.02);
  g.add(face);
  const hand = (len: number, w: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(cx, cy, D - 0.04);
    pivot.rotation.y = Math.PI;
    const h = new THREE.Mesh(new THREE.BoxGeometry(w, len, 0.004), paint(0x2b2d42, 0.4));
    h.position.y = len / 2;
    pivot.add(h);
    g.add(pivot);
    return pivot;
  };
  const hourHand = hand(0.08, 0.014);
  const minuteHand = hand(0.12, 0.009);

  const n = 4;
  return {
    group: g,
    hood,
    lamp,
    machineBox: m,
    hourHand,
    minuteHand,
    highlight,
    anchors: {
      sink: new THREE.Vector3((bx0 + bx1) / 2, bb + 0.13, (bz0 + bz1) / 2),
      rack: new THREE.Vector3((rk.min[0] + rk.max[0]) / 2, COUNTER_TOP, (rk.min[2] + rk.max[2]) / 2),
      machineRack: new THREE.Vector3((m.min[0] + m.max[0]) / 2, 0.86, (m.min[2] + m.max[2]) / 2),
      ticketRail,
    },
    shelfSpot(kind: DishKind, i: number) {
      // Clean cups stack upside down on the soda machine, two stacks of eight.
      if (kind === "tumbler") {
        const stack = Math.floor(i / 8) % 2;
        return { pos: new THREE.Vector3(0.2, 1.75 + 0.14 + (i % 8) * 0.016, sz0 + 0.25 + stack * 0.55), flip: true, turn: 0 };
      }
      // Clean baskets nest in stacks of six, two stacks a shelf.
      const level = 1 + Math.floor(i / 12);
      const col = Math.floor(i / 6) % 2;
      return { pos: new THREE.Vector3(sh.min[0] + 0.33 + col * 0.62, shelfLevels[level]! + (i % 6) * 0.022, (sh.min[2] + sh.max[2]) / 2), flip: false, turn: 0 };
    },
    outside,
    keyLight: { from: new THREE.Vector3(3.2, 2.7, 2.6), at: new THREE.Vector3(3, 0.4, 1.8) },
    passLight: new THREE.Vector3((x0 + x1) / 2, 1.5, D + 0.4),
    cooking: {
      griddle: Array.from({ length: n }, (_, i) => new THREE.Vector3(...slotCentre(top, i, n)).setY(0.9)),
      fryer: [0, 1].map((i) => new THREE.Vector3(...slotCentre(fr, i, 2)).setY(0.865)),
      board: Array.from({ length: BOARD_SPOTS }, (_, i) => new THREE.Vector3(...slotCentre(bd, i, BOARD_SPOTS)).setY(bd.max[1] - 0.075).setZ((bd.min[2] + bd.max[2]) / 2 - 0.02)),
      bell,
      ledge: [0, 1, 2].map((i) => new THREE.Vector3(x0 + 0.14 + i * 0.14, y0, 4.24)),
    },
  };
}

/** A small painted label on a station's face. */
function label(parent: THREE.Object3D, text: string, x: number, y: number, z: number): void {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 32;
  const c = canvas.getContext("2d");
  if (c) {
    c.fillStyle = "#2b2d42";
    c.fillRect(0, 0, 128, 32);
    c.fillStyle = "#f8f4e8";
    c.font = "bold 18px sans-serif";
    c.textAlign = "center";
    c.fillText(text, 64, 23);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 0.06), new THREE.MeshBasicMaterial({ map: tex }));
  m.position.set(x, y, z);
  parent.add(m);
}
